import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DigitalSignatureCapture = ({ onSignatureCapture, requiredSignatures = ['technician'], isVisible = false }) => {
  const [signatures, setSignatures] = useState({});
  const [currentSigner, setCurrentSigner] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [authenticationStep, setAuthenticationStep] = useState('credentials'); // credentials, signature, complete
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  
  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  // Mock user credentials for different roles
  const mockCredentials = {
    'technician': { username: 'tech.maria', password: 'TechPass123!' },
    'supervisor': { username: 'super.nikos', password: 'SuperPass456!' },
    'qa_manager': { username: 'qa.elena', password: 'QAPass789!' }
  };

  useEffect(() => {
    if (isVisible && canvasRef?.current) {
      const canvas = canvasRef?.current;
      const context = canvas?.getContext('2d');
      
      // Set canvas size
      canvas.width = canvas?.offsetWidth;
      canvas.height = canvas?.offsetHeight;
      
      // Configure drawing context
      context.lineCap = 'round';
      context.strokeStyle = '#1F2937';
      context.lineWidth = 2;
      contextRef.current = context;
    }
  }, [isVisible, currentSigner]);

  const startDrawing = (e) => {
    if (!contextRef?.current) return;
    
    setIsDrawing(true);
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;
    
    contextRef?.current?.beginPath();
    contextRef?.current?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !contextRef?.current) return;
    
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;
    
    contextRef?.current?.lineTo(x, y);
    contextRef?.current?.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    contextRef?.current?.closePath();
    
    // Capture signature data
    const canvas = canvasRef?.current;
    const dataURL = canvas?.toDataURL();
    setSignatureData(dataURL);
  };

  const clearSignature = () => {
    if (!contextRef?.current || !canvasRef?.current) return;
    
    contextRef?.current?.clearRect(0, 0, canvasRef?.current?.width, canvasRef?.current?.height);
    setSignatureData('');
  };

  const validateCredentials = () => {
    const newErrors = {};
    
    if (!credentials?.username?.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!credentials?.password?.trim()) {
      newErrors.password = 'Password is required';
    }
    
    // Check against mock credentials
    const expectedCreds = mockCredentials?.[currentSigner];
    if (expectedCreds) {
      if (credentials?.username !== expectedCreds?.username) {
        newErrors.username = 'Invalid username';
      }
      if (credentials?.password !== expectedCreds?.password) {
        newErrors.password = 'Invalid password';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleCredentialSubmit = () => {
    if (validateCredentials()) {
      setAuthenticationStep('signature');
      setErrors({});
    }
  };

  const handleSignatureSubmit = () => {
    if (!signatureData) {
      setErrors({ signature: 'Signature is required' });
      return;
    }
    
    const timestamp = new Date()?.toISOString();
    const newSignature = {
      role: currentSigner,
      username: credentials?.username,
      signatureData,
      timestamp,
      ipAddress: '192.168.1.100', // Mock IP
      verified: true
    };
    
    setSignatures(prev => ({
      ...prev,
      [currentSigner]: newSignature
    }));
    
    setAuthenticationStep('complete');
    setCredentials({ username: '', password: '' });
    setSignatureData('');
    clearSignature();
    
    // Check if all required signatures are collected
    const updatedSignatures = { ...signatures, [currentSigner]: newSignature };
    const allSigned = requiredSignatures?.every(role => updatedSignatures?.[role]);
    
    if (allSigned && onSignatureCapture) {
      onSignatureCapture(updatedSignatures);
    }
  };

  const startSignatureProcess = (signerRole) => {
    setCurrentSigner(signerRole);
    setAuthenticationStep('credentials');
    setCredentials({ username: '', password: '' });
    setErrors({});
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'technician': 'Laboratory Technician',
      'supervisor': 'Lab Supervisor',
      'qa_manager': 'QA Manager'
    };
    return roleNames?.[role] || role;
  };

  const getSignatureStatus = (role) => {
    if (signatures?.[role]) {
      return {
        status: 'signed',
        color: 'text-success',
        bgColor: 'bg-success/10',
        icon: 'CheckCircle'
      };
    }
    if (currentSigner === role) {
      return {
        status: 'in_progress',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        icon: 'Clock'
      };
    }
    return {
      status: 'pending',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted/10',
      icon: 'Circle'
    };
  };

  if (!isVisible) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="PenTool" size={16} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Digital Signature Capture</h3>
            <p className="text-sm text-muted-foreground">
              Secure authentication and signature verification
            </p>
          </div>
        </div>
      </div>
      {/* Signature Requirements */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-3">Required Signatures</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {requiredSignatures?.map((role) => {
            const status = getSignatureStatus(role);
            return (
              <div
                key={role}
                className={`p-3 border rounded-lg ${status?.bgColor} ${
                  currentSigner === role ? 'border-primary' : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-foreground">{getRoleDisplayName(role)}</h5>
                    <p className="text-xs text-muted-foreground capitalize">{status?.status?.replace('_', ' ')}</p>
                  </div>
                  <Icon name={status?.icon} size={16} className={status?.color} />
                </div>
                {!signatures?.[role] && currentSigner !== role && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startSignatureProcess(role)}
                    className="w-full mt-2"
                  >
                    Sign as {getRoleDisplayName(role)}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Authentication Process */}
      {currentSigner && (
        <div className="border border-primary/20 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-foreground mb-4">
            Signing as: {getRoleDisplayName(currentSigner)}
          </h4>

          {authenticationStep === 'credentials' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Username"
                  type="text"
                  placeholder="Enter your username"
                  value={credentials?.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e?.target?.value }))}
                  error={errors?.username}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={credentials?.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e?.target?.value }))}
                  error={errors?.password}
                  required
                />
              </div>
              
              <div className="bg-muted/50 rounded-md p-3">
                <p className="text-xs text-muted-foreground mb-2">Mock Credentials for Testing:</p>
                <p className="text-xs font-mono text-foreground">
                  Username: {mockCredentials?.[currentSigner]?.username} | 
                  Password: {mockCredentials?.[currentSigner]?.password}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setCurrentSigner('')}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleCredentialSubmit}
                >
                  Authenticate
                </Button>
              </div>
            </div>
          )}

          {authenticationStep === 'signature' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Please provide your digital signature
                </label>
                <div className="border border-border rounded-lg p-4 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={200}
                    className="w-full h-48 border border-dashed border-muted-foreground/30 rounded cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                </div>
                {errors?.signature && (
                  <p className="text-sm text-error mt-1">{errors?.signature}</p>
                )}
              </div>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={clearSignature}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Clear Signature
                </Button>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setAuthenticationStep('credentials')}
                  >
                    Back
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleSignatureSubmit}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Confirm Signature
                  </Button>
                </div>
              </div>
            </div>
          )}

          {authenticationStep === 'complete' && (
            <div className="text-center py-4">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
              <p className="text-foreground font-medium">Signature Captured Successfully</p>
              <p className="text-sm text-muted-foreground">
                {getRoleDisplayName(currentSigner)} signature verified and stored
              </p>
              <Button
                variant="outline"
                onClick={() => setCurrentSigner('')}
                className="mt-3"
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Completed Signatures */}
      {Object.keys(signatures)?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h4 className="font-medium text-foreground mb-4">Completed Signatures</h4>
          <div className="space-y-3">
            {Object.entries(signatures)?.map(([role, signature]) => (
              <div key={role} className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <div>
                    <p className="font-medium text-foreground">{getRoleDisplayName(role)}</p>
                    <p className="text-sm text-muted-foreground">
                      Signed by {signature?.username} on {new Date(signature.timestamp)?.toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  View Signature
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalSignatureCapture;